import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Line, LineConfig } from '@ant-design/charts';
import { Card, Flex, Button, Spin, Tabs, Empty, message } from '@app/ui';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import * as Sentry from '@sentry/browser';
import TopItemButton from './TopItemButton';
import { useBroadcastsContext } from '../../BroadcastsContext';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';
import { rhAttributionPostQueryFields } from '@app/reports/rhAttributionPostQueryFields';
import {
    TopSponsorsGroupBy,
    useTopSponsorsCardStore,
    useSetUpTopSponsorsCard,
} from '../../hooks/useTopSponsorsCard';
import { colors10 } from '@app/constants/theme';
import { shortCurrency } from '@app/utils/formatters';
import { DownOutlined, RightOutlined } from '@ant-design/icons';

const minChartHeight = 250;

const TopSponsorsCard = () => {
    const { t } = useTranslation(['common', 'measurement']);
    const { filters, client } = useBroadcastsContext();
    const { rightsholderAttribution } = useRightsholderAttribution({ client });
    const attributionQueryFields = useMemo(
        () => rhAttributionPostQueryFields(client, rightsholderAttribution),
        [client, rightsholderAttribution],
    );
    useSetUpTopSponsorsCard(filters, attributionQueryFields);
    const {
        isLoadingMediaValueReportGraph,
        chartDataPoints,
        groupBy,
        setGroupBy,
        selectedSponsorIds,
        topSponsorsData,
        toggleSelectedSponsor,
        isLoadingMediaValueReportTopSponsors,
    } = useTopSponsorsCardStore();
    const chartRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);
    const refContainer = React.useRef<HTMLDivElement>(null);
    const [isCollapsed, setIsCollapsed] = useState(false);

    useEffect(() => {
        if (refContainer.current && topSponsorsData.length > 0) {
            setHeight(
                refContainer.current.clientHeight > minChartHeight
                    ? refContainer.current.clientHeight
                    : minChartHeight,
            );
        }
    }, [topSponsorsData, refContainer.current]);

    const handleDownloadPNG = async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (chartRef.current) {
            try {
                const canvas = await html2canvas(chartRef.current, {
                    backgroundColor: '#ffffff',
                    scale: 2, // Higher quality
                });

                const link = document.createElement('a');
                link.download = `top-sponsors-chart-${groupBy}-${new Date().toISOString().split('T')[0]}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();

                message.success(
                    t(
                        'measurement:broadcastExposure.verbalMentions.topSponsors.notifications.chartDownloaded',
                    ),
                );
            } catch (error) {
                message.error(
                    t(
                        'measurement:broadcastExposure.verbalMentions.topSponsors.notifications.downloadFailed',
                    ),
                );
                Sentry.captureException(error, scope => {
                    scope.setTag('TopSponsorsCard', 'downloadPNG');
                    scope.setLevel('error');
                    return scope;
                });
            }
        } else {
            message.error(
                t(
                    'measurement:broadcastExposure.verbalMentions.topSponsors.notifications.chartNotAvailable',
                ),
            );
        }
    };

    const gropByTabItems = (
        Object.values(TopSponsorsGroupBy) as Array<TopSponsorsGroupBy>
    ).map(value => {
        const translatedValue = t(
            `measurement:broadcastExposure.verbalMentions.topSponsors.groupBy.${value}`,
        );
        return {
            key: value,
            label: (
                <Flex align="center" justify="center" gap={12}>
                    {t(
                        'measurement:broadcastExposure.verbalMentions.topSponsors.tabLabel',
                        { value: translatedValue },
                    )}
                </Flex>
            ),
        };
    });

    const config: LineConfig = useMemo(() => {
        const [selectedColors, selectedNames] = topSponsorsData.reduce(
            (acc, { id, name }, index) => {
                if (selectedSponsorIds.has(id)) {
                    acc[0].push(colors10[index]);
                    acc[1].push(name);
                }
                return acc;
            },
            [[], []],
        );

        return {
            data: chartDataPoints,
            xField: 'date',
            yField:
                groupBy === TopSponsorsGroupBy.exposure ? 'exposures' : 'smv',
            colorField: 'name',
            point: {
                shapeField: 'circle',
                sizeField: 4,
            },
            style: {
                lineWidth: 2,
            },
            scale: {
                color: {
                    domain: selectedNames,
                    range: selectedColors,
                },
            },
            axis: {
                x: {
                    grid: true,
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelFontWeight: 500,
                    labelOpacity: 1,
                    labelTransform: 'rotate(-30)',
                    labelSpacing: 15,
                },
                y: {
                    title:
                        groupBy === TopSponsorsGroupBy.exposure
                            ? t(
                                  'measurement:broadcastExposure.verbalMentions.topSponsors.yAxis.exposures',
                              )
                            : t(
                                  'measurement:broadcastExposure.verbalMentions.topSponsors.yAxis.sponsorMediaValue',
                              ),
                    titleFontSize: 16,
                    titleFill: '#5E6066',
                    titleStroke: 'transparent',
                    labelFormatter: value =>
                        groupBy === TopSponsorsGroupBy.exposure
                            ? value
                            : shortCurrency(value, 1),
                    grid: true,
                    line: true,
                    tickLine: true,
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelFontWeight: 500,
                    labelOpacity: 1,
                },
            },
            tooltip: {
                channel: 'y',
                valueFormatter: v =>
                    groupBy === TopSponsorsGroupBy.exposure
                        ? v
                        : shortCurrency(v, 1),
            },
            legend: true,
            height,
        };
    }, [chartDataPoints, groupBy, height, t]);

    if (!isLoadingMediaValueReportTopSponsors && topSponsorsData.length === 0) {
        return (
            <Card
                title={t(
                    'measurement:broadcastExposure.verbalMentions.topSponsors.title',
                )}
                style={{ width: '100%' }}
            >
                <Flex justify="center" align="center" style={{ height: 200 }}>
                    <Empty description={t('noDataAvailable')} />
                </Flex>
            </Card>
        );
    }

    return (
        <Card
            title={t(
                'measurement:broadcastExposure.verbalMentions.topSponsors.title',
            )}
            extra={
                <Flex gap="middle">
                    <Button
                        onClick={handleDownloadPNG}
                        style={{
                            backgroundColor: 'rgba(0, 159, 223, 1)',
                            color: 'white',
                        }}
                    >
                        <i className="far fa-arrow-to-bottom" />
                        {t(
                            'measurement:broadcastExposure.verbalMentions.topSponsors.downloadPng',
                        )}
                    </Button>
                    <Button
                        style={{ color: 'rgb(81, 82, 82)' }}
                        icon={
                            isCollapsed ? <RightOutlined /> : <DownOutlined />
                        }
                        type="text"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                    />
                </Flex>
            }
            style={{ width: '100%' }}
            styles={{
                body: { ...(isCollapsed && { padding: 0 }) },
                header: {
                    ...(isCollapsed && {
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                    }),
                },
            }}
        >
            {!isCollapsed && (
                <Flex vertical>
                    <Tabs
                        activeKey={groupBy}
                        items={gropByTabItems}
                        onChange={(key: TopSponsorsGroupBy) => setGroupBy(key)}
                    />
                    <Flex gap={16} align="flex-start" wrap>
                        {
                            <Flex
                                vertical
                                style={{
                                    flexShrink: 2,
                                    flexGrow: 1,
                                    minHeight: 200,
                                }}
                                ref={refContainer}
                            >
                                {!isLoadingMediaValueReportTopSponsors &&
                                topSponsorsData.length > 0 ? (
                                    <Flex vertical>
                                        {topSponsorsData.map((item, index) => {
                                            return (
                                                <TopItemButton
                                                    key={item.id}
                                                    isSelected={selectedSponsorIds.has(
                                                        item.id,
                                                    )}
                                                    groupBy={groupBy}
                                                    indexItem={index}
                                                    item={item}
                                                    accentColor={
                                                        colors10[index]
                                                    }
                                                    toggleOption={
                                                        toggleSelectedSponsor
                                                    }
                                                />
                                            );
                                        })}
                                    </Flex>
                                ) : (
                                    <Spin size="large" />
                                )}
                            </Flex>
                        }
                        <Flex
                            justify="center"
                            align="center"
                            style={{ flexGrow: 2, minWidth: 300 }}
                        >
                            <div ref={chartRef}>
                                {isLoadingMediaValueReportGraph ? (
                                    <Flex
                                        align="center"
                                        style={{
                                            height,
                                        }}
                                    >
                                        <Spin size="large" />
                                    </Flex>
                                ) : chartDataPoints?.length > 0 ? (
                                    <Line {...config} />
                                ) : (
                                    <Flex
                                        align="center"
                                        style={{
                                            height,
                                        }}
                                    >
                                        <Empty
                                            description={t('noDataAvailable')}
                                        />
                                    </Flex>
                                )}
                            </div>
                        </Flex>
                    </Flex>
                </Flex>
            )}
        </Card>
    );
};

export default TopSponsorsCard;
